class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // Watch for DOM changes in cart-drawer-items specifically (not the whole drawer)
    const cartItems = this.querySelector('cart-drawer-items');
    if (cartItems) {
      this.drawerObserver = new MutationObserver(() => {
        // Debounce to prevent infinite loops
        clearTimeout(this.reinitTimeout);
        this.reinitTimeout = setTimeout(() => {
          // Temporarily disconnect to prevent infinite loop
          this.drawerObserver.disconnect();

          this.initRecommendationsCarousel();

          // Reconnect observer
          this.drawerObserver.observe(cartItems, {
            childList: true,
            subtree: true
          });
        }, 50);
      });

      this.drawerObserver.observe(cartItems, {
        childList: true,
        subtree: true
      });
    }
  }

  disconnectedCallback() {
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    const cartLinkMobile = document.querySelector('#cart-icon-bubble-mobile');

    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink)
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    if (cartLinkMobile) {
      cartLinkMobile.setAttribute('role', 'button');
      cartLinkMobile.setAttribute('aria-haspopup', 'dialog');
      cartLinkMobile.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLinkMobile)
      });
      cartLinkMobile.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLinkMobile);
        }
      });
    }

    const cartLinkMobileMenu = document.querySelector('#cart-icon-bubble-mobile-menu');
    if (cartLinkMobileMenu) {
      cartLinkMobileMenu.setAttribute('role', 'button');
      cartLinkMobileMenu.setAttribute('aria-haspopup', 'dialog');
      cartLinkMobileMenu.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLinkMobileMenu);
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    let focusTrapped = false;

    // Primary: Wait for transition end
    this.addEventListener('transitionend', () => {
      if (!focusTrapped) {
        focusTrapped = true;
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, { once: true });

    // Fallback: If transition doesn't fire within 300ms, trap focus anyway
    setTimeout(() => {
      if (!focusTrapped) {
        focusTrapped = true;
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, 300);

    document.body.classList.add('overflow-hidden');

    // Initialize recommendations carousel
    this.initRecommendationsCarousel();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  initRecommendationsCarousel() {
    const recommendationsSwiper = this.querySelector('.cart-drawer__recommendations-swiper');

    if (recommendationsSwiper && typeof Swiper !== 'undefined') {
      // Destroy existing instance if it exists
      if (recommendationsSwiper.swiper) {
        recommendationsSwiper.swiper.destroy(true, true);
      }

      // Initialize new Swiper instance
      new Swiper(recommendationsSwiper, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        grabCursor: true,
        navigation: {
          nextEl: '.cart-drawer__nav-next',
          prevEl: '.cart-drawer__nav-prev',
        },
        breakpoints: {
          640: {
            slidesPerView: 'auto',
            spaceBetween: 16
          }
        }
      });
    }

    // Wire up add to cart buttons
    this.initAddToCartButtons();
  }

  initAddToCartButtons() {
    const addButtons = this.querySelectorAll('.cart-drawer__product-add');

    addButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const variantId = button.dataset.variantId;
        if (!variantId) return;

        // Disable button during request
        button.disabled = true;
        button.style.opacity = '0.5';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: 1
              }]
            })
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          // Refresh cart drawer content
          const cartResponse = await fetch('/?sections=cart-drawer');
          const cartData = await cartResponse.json();

          // Update cart drawer
          if (cartData['cart-drawer']) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(cartData['cart-drawer'], 'text/html');
            const newDrawerInner = doc.querySelector('.drawer__inner');
            const newCartDrawer = doc.querySelector('cart-drawer');

            if (newDrawerInner) {
              const currentDrawerInner = this.querySelector('.drawer__inner');
              currentDrawerInner.innerHTML = newDrawerInner.innerHTML;

              // Update is-empty class on parent cart-drawer element
              if (newCartDrawer && !newCartDrawer.classList.contains('is-empty')) {
                this.classList.remove('is-empty');
              } else {
                this.classList.add('is-empty');
              }

              // Re-initialize carousel and buttons after content update
              this.initRecommendationsCarousel();
            }
          }

          // Update cart icon count using existing method
          this.getCartSize();

        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('Failed to add product to cart. Please try again.');
        } finally {
          button.disabled = false;
          button.style.opacity = '1';
        }
      });
    });
  }

  getCartSize(){
    const cartItemsIconUrl = `/?section_id=header-cart-icon`;

    fetch(cartItemsIconUrl)
    .then(res => {
        if (!res.ok) {
            var error = new Error(res.status);
            throw error;
        }
        return res.text();
    })
    .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html')
            .querySelector('#shopify-section-header-cart-icon').innerHTML;

        document.querySelector('custom-header .nav-item .cart-count').innerHTML = resultsMarkup.trim();
        document.querySelector('custom-header .nav-item .cart-count').setAttribute('data-hidden', 'false');
    })
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
